
import React, { useState, useEffect } from 'react';
import { api } from '../Contexts/AuthContext';
import { 
  Settings, 
  Calendar, 
  Coffee, 
  Save, 
  Plus, 
  Trash2, 
  Clock, 
  DollarSign,
  Info,
  ChevronRight,
  ShieldCheck,
  Zap,
  Globe,
  Sun,
  Scale,
  Pencil,
  X,
  Check
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const HRSettings = () => {
  const [loading, setLoading] = useState(true);
  const [orgData, setOrgData] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('rules');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', quota: '', frequency: 'MONTHLY' });
  
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '' });
  const [newLeaveType, setNewLeaveType] = useState({ 
    name: '', 
    quota: '', 
    frequency: 'MONTHLY', 
    carry_forward: false 
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgRes, holidayRes, leaveRes] = await Promise.all([
        api.get('organizations/me/'),
        api.get('hr/holidays/'),
        api.get('hr/leave-types/')
      ]);
      setOrgData(orgRes.data);
      setHolidays(holidayRes.data);
      setLeaveTypes(leaveRes.data);
    } catch (error) {
      console.error("Error fetching HR settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`organizations/me/`, {
        work_week_rule: orgData.work_week_rule,
        salary_basis: orgData.salary_basis
      });
      toast.success("Organization policies synchronized");
    } catch (error) {
      toast.error("Update failed");
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('hr/holidays/', newHoliday);
      setHolidays([...holidays, res.data]);
      setNewHoliday({ name: '', date: '' });
      toast.success("Holiday added");
    } catch (error) {
      toast.error("Failed to add holiday");
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await api.delete(`hr/holidays/${id}/`);
      setHolidays(holidays.filter(h => h.id !== id));
      toast.success("Holiday removed");
    } catch (error) {
      toast.error("Action denied");
    }
  };

  const handleAddLeaveType = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('hr/leave-types/', newLeaveType);
      setLeaveTypes([...leaveTypes, res.data]);
      setNewLeaveType({ name: '', quota: '', frequency: 'MONTHLY', carry_forward: false });
      toast.success("Leave type created");
    } catch (error) {
      toast.error("Failed to create type");
    }
  };

  const handleDeleteLeaveType = async (id) => {
    try {
      await api.delete(`hr/leave-types/${id}/`);
      setLeaveTypes(leaveTypes.filter(l => l.id !== id));
      toast.success("Type removed");
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleUpdateLeaveType = async (id) => {
    try {
      const res = await api.patch(`hr/leave-types/${id}/`, editForm);
      setLeaveTypes(leaveTypes.map(l => l.id === id ? res.data : l));
      setEditingId(null);
      toast.success("Leave type updated");
    } catch (error) {
      toast.error("Failed to update type");
    }
  };

  if (loading) return (
    <div className="hr-loader">
        <div className="spinner"></div>
        <p>Synchronizing HR Data...</p>
        <style>{`
            .hr-loader { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 80vh; gap: 20px; font-family: 'DM Sans', sans-serif; }
            .spinner { width: 48px; height: 48px; border: 4px solid #dde8b8; border-top: 4px solid #799F0C; border-radius: 50%; animation: spin 1s linear infinite; }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            .hr-loader p { color: #4e6010; font-weight: 600; font-size: 14px; }
        `}</style>
    </div>
  );

  return (
    <div className="hr-settings-page">
      <style>{CSS}</style>
      
      {/* Page Header */}
      <div className="page-head">
        <div className="head-left">
           <div className="page-title">HR & Governance</div>
           <p className="page-sub">Manage organization-wide work architecture and policies</p>
        </div>
        <div className="head-right">
           <div className="status-badge">
              <ShieldCheck size={14} />
              <span>ACTIVE POLICY ENGINE</span>
           </div>
        </div>
      </div>

      <div className="hr-main-grid">
        {/* Navigation Sidebar */}
        <div className="hr-sidebar">
           <button 
             className={`nav-item ${activeTab === 'rules' ? 'active' : ''}`}
             onClick={() => setActiveTab('rules')}
           >
              <div className="item-ico"><Zap size={18} /></div>
              <span>Work Architecture</span>
           </button>
           <button 
             className={`nav-item ${activeTab === 'holidays' ? 'active' : ''}`}
             onClick={() => setActiveTab('holidays')}
           >
              <div className="item-ico"><Calendar size={18} /></div>
              <span>Holiday Calendar</span>
           </button>
           <button 
             className={`nav-item ${activeTab === 'leaves' ? 'active' : ''}`}
             onClick={() => setActiveTab('leaves')}
           >
              <div className="item-ico"><Coffee size={18} /></div>
              <span>Leave Quotas</span>
           </button>
        </div>

        {/* Content Area */}
        <div className="hr-content">
           
           {activeTab === 'rules' && (
              <div className="card-panel animate-in">
                <div className="panel-header">
                  <div className="panel-ico rules"><Scale size={20} /></div>
                  <div className="panel-titles">
                    <h3>Rules & Salary Algorithm</h3>
                    <p>Foundational logic for payroll and attendance generation.</p>
                  </div>
                </div>

                <form onSubmit={handleUpdateOrg} className="panel-form">
                  <div className="form-row">
                    <div className="field-group">
                      <label>WORK WEEK MODEL</label>
                      <select 
                        value={orgData?.work_week_rule}
                        onChange={(e) => setOrgData({...orgData, work_week_rule: e.target.value})}
                      >
                        <option value="5_DAYS">Standard 5-Day (Mon-Fri)</option>
                        <option value="6_DAYS">Extended 6-Day (Mon-Sat)</option>
                        <option value="7_DAYS">Full 7-Day (Mon-Sun)</option>
                        <option value="2ND_SAT_OFF">Semi-Classic (2nd Sat Off)</option>
                        <option value="2ND_4TH_SAT_OFF">Progressive (2nd & 4th Sat Off)</option>
                        <option value="ALL_SAT_OFF">Modern (All Saturdays Off)</option>
                      </select>
                    </div>

                    <div className="field-group">
                      <label>SALARY BASIS ALGORITHM</label>
                      <select 
                        value={orgData?.salary_basis}
                        onChange={(e) => setOrgData({...orgData, salary_basis: e.target.value})}
                      >
                        <option value="FIXED_30_DAYS">Fixed 30-Day Model (Inclusive)</option>
                        <option value="WORKING_DAYS_ONLY">Actual Working Days (Exclusive)</option>
                      </select>
                    </div>
                  </div>

                  <div className="panel-info">
                     <Info size={16} />
                     <span>These rules directly impact daily rate calculations for deductions and overtime.</span>
                  </div>

                  <button type="submit" className="primary-btn">
                    <Save size={18} /> 
                    <span>Save Governance Policies</span>
                  </button>
                </form>
              </div>
           )}

           {activeTab === 'holidays' && (
              <div className="card-panel animate-in">
                <div className="panel-header">
                  <div className="panel-ico holidays"><Sun size={20} /></div>
                  <div className="panel-titles">
                    <h3>Corporate Holiday Calendar</h3>
                    <p>Define organization-wide paid non-working days.</p>
                  </div>
                </div>

                <form onSubmit={handleAddHoliday} className="add-strip">
                  <input 
                    type="text" placeholder="e.g. Independence Day" 
                    value={newHoliday.name} onChange={e => setNewHoliday({...newHoliday, name: e.target.value})}
                    required
                  />
                  <input 
                    type="date" 
                    value={newHoliday.date} onChange={e => setNewHoliday({...newHoliday, date: e.target.value})}
                    required
                  />
                  <button type="submit" className="add-btn"><Plus size={20} /></button>
                </form>

                <div className="holiday-list">
                  {holidays.length === 0 && <div className="empty-state">No corporate holidays defined.</div>}
                  {holidays.map(h => (
                    <div key={h.id} className="holiday-row">
                      <div className="h-info">
                        <div className="h-date-box">
                           <span className="h-day">{new Date(h.date).getDate()}</span>
                           <span className="h-month">{new Date(h.date).toLocaleString('default', { month: 'short' })}</span>
                        </div>
                        <div className="h-text">
                           <strong>{h.name}</strong>
                           <span>Year {new Date(h.date).getFullYear()}</span>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteHoliday(h.id)} className="purge-btn">
                         <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
           )}

           {activeTab === 'leaves' && (
              <div className="card-panel animate-in">
                <div className="panel-header">
                  <div className="panel-ico leaves"><Coffee size={20} /></div>
                  <div className="panel-titles">
                    <h3>Leave Quotas & Buckets</h3>
                    <p>Automated allocation of monthly and annual leave credits.</p>
                  </div>
                </div>

                <form onSubmit={handleAddLeaveType} className="leave-init-form">
                  <div className="init-grid">
                     <div className="field-group">
                        <label>CATEGORY NAME</label>
                        <input 
                           type="text" placeholder="e.g. Wellness Leave" 
                           value={newLeaveType.name} onChange={e => setNewLeaveType({...newLeaveType, name: e.target.value})}
                           required
                         />
                     </div>
                     <div className="field-group">
                        <label>QUOTA (DAYS)</label>
                        <input 
                           type="number" step="0.5" placeholder="1.5" 
                           value={newLeaveType.quota} onChange={e => setNewLeaveType({...newLeaveType, quota: e.target.value})}
                           required
                         />
                     </div>
                     <div className="field-group">
                        <label>REFRESH CYCLE</label>
                        <select value={newLeaveType.frequency} onChange={e => setNewLeaveType({...newLeaveType, frequency: e.target.value})}>
                           <option value="MONTHLY">Monthly Refresh</option>
                           <option value="ANNUALLY">Annual Allotment</option>
                        </select>
                     </div>
                  </div>
                  <button type="submit" className="primary-btn full-w">
                     <Plus size={18} />
                     <span>Allocate New Leave Bucket</span>
                  </button>
                </form>

                 <div className="quota-grid">
                   {leaveTypes.map(l => (
                     <div key={l.id} className="quota-card">
                       {editingId === l.id ? (
                         <div>
                           <label style={{ fontSize: '9px', fontWeight: '800', color: '#8a9e50', letterSpacing: '0.5px' }}>CATEGORY NAME</label>
                           <input 
                             className="edit-input" 
                             value={editForm.name} 
                             onChange={e => setEditForm({ ...editForm, name: e.target.value })} 
                           />
                           <label style={{ fontSize: '9px', fontWeight: '800', color: '#8a9e50', letterSpacing: '0.5px' }}>QUOTA (DAYS)</label>
                           <input 
                             type="number" 
                             step="0.5" 
                             className="edit-input" 
                             value={editForm.quota} 
                             onChange={e => setEditForm({ ...editForm, quota: e.target.value })} 
                           />
                           <label style={{ fontSize: '9px', fontWeight: '800', color: '#8a9e50', letterSpacing: '0.5px' }}>REFRESH CYCLE</label>
                           <select 
                             className="edit-select" 
                             value={editForm.frequency} 
                             onChange={e => setEditForm({ ...editForm, frequency: e.target.value })}
                           >
                             <option value="MONTHLY">Monthly Refresh</option>
                             <option value="ANNUALLY">Annual Allotment</option>
                           </select>
                           <div className="edit-actions">
                             <button className="edit-btn save" onClick={() => handleUpdateLeaveType(l.id)} title="Save changes">
                               <Check size={18} />
                             </button>
                             <button className="edit-btn cancel" onClick={() => setEditingId(null)} title="Cancel">
                               <X size={18} />
                             </button>
                           </div>
                         </div>
                       ) : (
                         <>
                           <div className="q-top">
                              <div className="q-val">{l.quota}</div>
                              <div>
                                 <button 
                                   onClick={() => {
                                     setEditingId(l.id);
                                     setEditForm({ name: l.name, quota: l.quota, frequency: l.frequency });
                                   }} 
                                   className="q-edit"
                                   title="Edit category"
                                 >
                                    <Pencil size={16} />
                                 </button>
                                 <button onClick={() => handleDeleteLeaveType(l.id)} className="q-delete" title="Delete category">
                                    <Trash2 size={16} />
                                 </button>
                              </div>
                           </div>
                           <div className="q-bottom">
                              <h4>{l.name}</h4>
                              <span className="q-freq">{l.frequency} CYCLE</span>
                           </div>
                         </>
                       )}
                     </div>
                   ))}
                 </div>
              </div>
           )}

        </div>
      </div>
    </div>
  );
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=DM+Sans:wght@400;500;700&display=swap');

.hr-settings-page {
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'DM Sans', sans-serif;
  color: #263200;
  background: #f2f6e8;
  min-height: 100vh;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 25px;
}

.page-title {
  font-family: 'Nunito', sans-serif;
  font-size: 24px;
  font-weight: 900;
  color: #263200;
}

.page-sub {
  font-size: 13px;
  color: #8a9e50;
  margin-top: 3px;
}

.status-badge {
  background: #fff;
  border: 1.5px solid #dde8b8;
  border-radius: 50px;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 800;
  color: #4e6010;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.03);
}

.hr-main-grid {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 30px;
}

.hr-sidebar {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 15px;
  border: none;
  background: transparent;
  border-radius: 12px;
  color: #4e6010;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.nav-item:hover {
  background: rgba(121, 159, 12, 0.08);
  color: #263200;
}

.nav-item.active {
  background: #799F0C;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(121, 159, 12, 0.25);
}

.item-ico {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-item.active .item-ico {
  background: rgba(255,255,255,0.2);
}

.card-panel {
  background: #fff;
  border-radius: 18px;
  padding: 35px;
  border: 1.5px solid #dde8b8;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 35px;
}

.panel-ico {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-ico.rules { background: #eef5d0; color: #799F0C; }
.panel-ico.holidays { background: #fffbe6; color: #d4ba00; }
.panel-ico.leaves { background: #f2f7e4; color: #4e6010; }

.panel-titles h3 {
  font-family: 'Nunito', sans-serif;
  font-size: 18px;
  font-weight: 900;
  color: #263200;
  margin: 0;
}

.panel-titles p {
  font-size: 13px;
  color: #8a9e50;
  margin-top: 3px;
}

.panel-form { display: flex; flex-direction: column; gap: 25px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

.field-group { display: flex; flex-direction: column; gap: 8px; }
.field-group label {
  font-size: 10px;
  font-weight: 800;
  color: #8a9e50;
  letter-spacing: 1px;
}

.field-group select, .field-group input, .add-strip input {
  padding: 12px 16px;
  border-radius: 10px;
  border: 1.5px solid #dde8b8;
  background: #fdfdfd;
  font-size: 14px;
  font-weight: 600;
  color: #263200;
  outline: none;
  transition: all 0.2s;
}

.field-group select:focus, .field-group input:focus {
  border-color: #799F0C;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(121, 159, 12, 0.05);
}

.panel-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  background: #fdfaf0;
  border: 1px solid #f2e9c9;
  border-radius: 10px;
  font-size: 12px;
  color: #947a00;
  font-weight: 500;
}

.primary-btn {
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: #799F0C;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(121, 159, 12, 0.2);
}

.primary-btn:hover {
  background: #5c7a09;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(121, 159, 12, 0.3);
}

.primary-btn.full-w { width: 100%; justify-content: center; margin-top: 10px; }

.add-strip {
  display: flex;
  gap: 12px;
  background: #fcfdfe;
  padding: 15px;
  border-radius: 14px;
  border: 1.5px solid #dde8b8;
  margin-bottom: 25px;
}

.add-strip input { flex: 1; }
.add-btn {
  width: 44px; height: 44px;
  background: #FFE000;
  color: #263200;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(255, 224, 0, 0.3);
}

.holiday-list { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.holiday-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: #fff;
  border: 1.5px solid #dde8b8;
  border-radius: 14px;
  transition: 0.2s;
}

.holiday-row:hover { border-color: #799F0C; transform: translateY(-2px); box-shadow: 0 5px 15px rgba(0,0,0,0.05); }

.h-info { display: flex; align-items: center; gap: 15px; }
.h-date-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 44px; height: 44px;
  background: #f2f7e4;
  border-radius: 10px;
  color: #799F0C;
}

.h-day { font-size: 16px; font-weight: 900; line-height: 1; }
.h-month { font-size: 10px; font-weight: 800; text-transform: uppercase; }

.h-text strong { font-size: 14px; font-weight: 700; display: block; }
.h-text span { font-size: 11px; color: #8a9e50; }

.purge-btn, .q-delete { background: transparent; border: none; color: #dde8b8; cursor: pointer; transition: 0.2s; }
.purge-btn:hover, .q-delete:hover { color: #e53935; }

.leave-init-form { margin-bottom: 35px; }
.init-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 15px; margin-bottom: 15px; }

.quota-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
.quota-card {
  background: #fdfdfd;
  border: 1.5px solid #dde8b8;
  border-radius: 16px;
  padding: 20px;
  transition: 0.2s;
}

.quota-card:hover { border-color: #799F0C; transform: translateY(-4px); box-shadow: 0 6px 18px rgba(0,0,0,0.06); }

.q-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
.q-val {
  width: 40px; height: 40px;
  background: #eef5d0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: #799F0C;
  font-size: 16px;
}

.q-bottom h4 { font-size: 16px; font-weight: 700; margin: 0; }
.q-freq { font-size: 9px; font-weight: 800; color: #8a9e50; letter-spacing: 1px; text-transform: uppercase; }

.animate-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.empty-state { grid-column: span 2; text-align: center; padding: 30px; color: #8a9e50; font-style: italic; font-size: 13px; }

.edit-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #dde8b8;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #263200;
  margin-top: 4px;
  margin-bottom: 10px;
  outline: none;
  box-sizing: border-box;
}
.edit-input:focus {
  border-color: #799F0C;
  background: #fff;
}
.edit-select {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #dde8b8;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #263200;
  margin-top: 4px;
  margin-bottom: 12px;
  outline: none;
  background: #fff;
  box-sizing: border-box;
}
.edit-select:focus {
  border-color: #799F0C;
}
.edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}
.edit-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.edit-btn.save { color: #799F0C; }
.edit-btn.save:hover { background: rgba(121, 159, 12, 0.1); }
.edit-btn.cancel { color: #8a9e50; }
.edit-btn.cancel:hover { background: rgba(138, 158, 80, 0.1); }
.q-edit { background: transparent; border: none; color: #dde8b8; cursor: pointer; transition: 0.2s; margin-right: 8px; }
.q-edit:hover { color: #799F0C; }
`;

export default HRSettings;
